import {
  AlertTriangle,
  Bell,
  Building2,
  CalendarDays,
  Camera,
  CarFront,
  CaseSensitive,
  CheckCircle2,
  ChevronRight,
  CircleGauge,
  FileText,
  Gauge,
  Home,
  Info,
  LayoutGrid,
  LogOut,
  Menu,
  MonitorSmartphone,
  Moon,
  MoreHorizontal,
  MousePointerClick,
  Plus,
  RefreshCw,
  Search,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Type,
  Users,
  WalletCards,
  Wrench,
  Wifi,
  WifiOff,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  formatCurrency,
  vehicleStatuses,
  type VehicleStatus,
} from "../shared/domain";
import type { VehicleListItem } from "../shared/types";
import { apiFetch, flushOfflineQueue, privateObjectUrl } from "./api";
import { useAuth } from "./auth";
import { CommandPalette, platformShortcut } from "./components/CommandPalette";
import { NotificationCenter } from "./components/NotificationCenter";
import { LogoutDialog } from "./components/LogoutDialog";
import { VehicleIntakeModal } from "./components/VehicleIntakeModal";
import { StatusBadge } from "./components/StatusBadge";
import { isMobileDestinationActive, isPrimaryDestinationActive, type PrimaryDestinationId } from "./navigation";
import { countQueuedMutations } from "./offline-queue";
import { useUiPreferences } from "./ui-preferences";

export { StatusBadge } from "./components/StatusBadge";

interface Toast {
  id: string;
  message: string;
  tone: "success" | "error" | "info";
}

const ToastContext = createContext<(message: string, tone?: Toast["tone"]) => void>(() => undefined);

const toastIcons: Record<Toast["tone"], LucideIcon> = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);
  const show = useCallback((message: string, tone: Toast["tone"] = "success") => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { id, message, tone }]);
    // Un error necesita tiempo para entenderse: permanece hasta descartarlo.
    if (tone !== "error") window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), tone === "info" ? 6500 : 4500);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map((toast) => {
          const Icon = toastIcons[toast.tone];
          return (
            <div className={`toast toast--${toast.tone}`} key={toast.id}>
              <Icon aria-hidden="true" />
              <span>{toast.message}</span>
              <button type="button" className="toast__close" onClick={() => dismiss(toast.id)} aria-label="Descartar aviso"><X aria-hidden="true" /></button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

export function moveTabFocus(event: ReactKeyboardEvent<HTMLButtonElement>) {
  const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []);
  const currentIndex = tabs.indexOf(event.currentTarget);
  let nextIndex: number | null = null;
  if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (currentIndex + 1) % tabs.length;
  if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  if (event.key === "Home") nextIndex = 0;
  if (event.key === "End") nextIndex = tabs.length - 1;
  if (nextIndex === null) return;
  const nextTab = tabs[nextIndex];
  if (!nextTab) return;
  event.preventDefault();
  nextTab.focus();
  nextTab.click();
}

export function Logo({ compact = false, lockup = false }: { compact?: boolean; lockup?: boolean }) {
  return (
    <div className={`brand ${compact ? "brand--compact" : ""} ${lockup ? "brand--lockup" : ""}`} aria-label="Cochegest">
      <img className="brand__asset" src="/brand/cochegest-refined-ui.webp?v=20260915" width={256} height={256} alt="" aria-hidden="true" decoding="async" />
      {lockup && <span className="brand__copy"><strong>Cochegest</strong><small>Gestión del concesionario</small></span>}
    </div>
  );
}

const primaryNavItems: Array<{ id: PrimaryDestinationId; to: string; label: string; bottomLabel?: string; icon: LucideIcon; bottom?: boolean }> = [
  { id: "home", to: "/app", label: "Inicio", icon: Home },
  { id: "agenda", to: "/app/agenda", label: "Agenda", icon: CalendarDays },
  { id: "vehicles", to: "/app/stock", label: "Vehículos", icon: CarFront },
  { id: "clients", to: "/app/leads?view=clientes", label: "Clientes", bottomLabel: "Comercial", icon: Users },
  { id: "sales", to: "/app/leads?view=ventas", label: "Ventas", icon: WalletCards, bottom: false },
  { id: "more", to: "/app/mas", label: "Funciones", icon: LayoutGrid },
];

const advancedNavItems: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: "/app/documentos", label: "Documentos", icon: FileText },
  { to: "/app/inteligencia", label: "Inteligencia", icon: CircleGauge },
  { to: "/app/configuracion", label: "Configuración", icon: Settings },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { mode, setMode, theme, setTheme, textSize, setTextSize } = useUiPreferences();
  const toast = useToast();
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  const [unread, setUnread] = useState(0);
  const [commandOpen, setCommandOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const alertsTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const updateQueueCount = () => void countQueuedMutations().then(setQueued);
    const offlineConflict = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      toast(detail?.message ? `Un cambio offline fue rechazado: ${detail.message}` : "Un cambio offline fue rechazado por el servidor.", "error");
      updateQueueCount();
    };
    const update = () => {
      const isOnline = navigator.onLine;
      setOnline(isOnline);
      if (isOnline) {
        void flushOfflineQueue().then((completed) => {
          updateQueueCount();
          if (completed > 0) {
            toast(`${completed} ${completed === 1 ? "cambio sincronizado" : "cambios sincronizados"}.`);
          }
        });
      }
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    window.addEventListener("cochegest:offline-conflict", offlineConflict);
    const refreshVisibleQueue = () => { if (document.visibilityState === "visible") updateQueueCount(); };
    const timer = window.setInterval(refreshVisibleQueue, 15_000);
    document.addEventListener("visibilitychange", refreshVisibleQueue);
    updateQueueCount();
    if (navigator.onLine) update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      window.removeEventListener("cochegest:offline-conflict", offlineConflict);
      document.removeEventListener("visibilitychange", refreshVisibleQueue);
      window.clearInterval(timer);
    };
  }, [toast]);

  useEffect(() => {
    const refresh = () => void apiFetch<{ unread: number }>("/notifications/summary").then((result) => setUnread(result.unread)).catch(() => undefined);
    const sync = (event: Event) => {
      const value = (event as CustomEvent<{ unread?: unknown }>).detail?.unread;
      if (typeof value === "number") setUnread(value);
      else refresh();
    };
    const refreshWhenVisible = () => { if (document.visibilityState === "visible") refresh(); };
    refresh();
    const timer = window.setInterval(refreshWhenVisible, 60_000);
    window.addEventListener("cochegest:notifications", sync);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("cochegest:notifications", sync);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
    setProfileOpen(false);
    setAlertsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!profileOpen) return;
    window.requestAnimationFrame(() => profileMenuRef.current?.querySelector<HTMLElement>("button, a[href]")?.focus());
  }, [profileOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase("en") === "k") {
        event.preventDefault();
        setCommandOpen((current) => !current);
      }
      if (event.key === "Escape" && profileOpen) {
        event.preventDefault();
        setProfileOpen(false);
        window.requestAnimationFrame(() => profileTriggerRef.current?.focus());
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".account-trigger") || profileMenuRef.current?.contains(target)) return;
      setProfileOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [profileOpen]);

  const toggleProfile = (trigger: HTMLButtonElement) => {
    profileTriggerRef.current = trigger;
    setProfileOpen((current) => !current);
  };
  const chooseMode = (nextMode: "essential" | "advanced") => {
    setMode(nextMode);
    setProfileOpen(false);
    window.requestAnimationFrame(() => profileTriggerRef.current?.focus());
  };
  const roleLabel = user?.role === "admin" ? "Administrador" : user?.role === "sales" ? "Comercial" : "Equipo";
  const requestLogout = () => { setProfileOpen(false); setLogoutOpen(true); };

  return (
    <div className={`app-shell app-shell--${mode}`}>
      <a className="skip-link" href="#main-content">Saltar al contenido</a>
      <aside className="sidebar">
        <Link className="sidebar__brand-link" to="/app" aria-label="Cochegest, ir al inicio"><Logo compact lockup /></Link>
        <div className="sidebar__workspace">
          <span><Building2 aria-hidden="true" /></span>
          <div><small>Concesionario</small><strong>{user?.tenant.name ?? "Mi concesionario"}</strong></div>
        </div>
        <p className="sidebar__section-label">Trabajo diario</p>
        <nav className="sidebar__nav" aria-label="Navegación principal">
          {primaryNavItems.map(({ id, to, label, icon: Icon }) => {
            const active = isPrimaryDestinationActive(id, location.pathname, location.search, mode);
            return <Link key={id} to={to} className={`nav-item ${active ? "nav-item--active" : ""}`} aria-current={active ? "page" : undefined}>
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </Link>;
          })}
        </nav>
        <nav className="sidebar__nav sidebar__nav--operations" aria-label="Preparación y publicación">
          <NavLink to="/app/taller" className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`}><Wrench aria-hidden="true" /><span>Talleres</span></NavLink>
          <NavLink to="/app/proveedores" className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`}><Building2 aria-hidden="true" /><span>Proveedores</span></NavLink>
          <NavLink to="/app/anuncios" className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`}><Camera aria-hidden="true" /><span>Publicaciones</span></NavLink>
        </nav>
        <div className="sidebar__lower">
          <NavLink to="/app/cuenta" className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`}><Users aria-hidden="true" /><span>Mi cuenta</span></NavLink>
          <button type="button" className="nav-item" onClick={requestLogout}><LogOut aria-hidden="true" /><span>Cerrar sesión</span></button>
          {mode === "advanced" && <>
            <p className="sidebar__section-label">Modo avanzado</p>
            <nav className="sidebar__nav sidebar__nav--advanced" aria-label="Navegación avanzada">
              {advancedNavItems.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`}><Icon /><span>{label}</span></NavLink>)}
            </nav>
          </>}
          {(!online || queued > 0) && <div className="sidebar-sync-warning"><WifiOff /><span>{online ? `${queued} cambios por enviar` : "Trabajando sin conexión"}</span></div>}
          <button type="button" className="sidebar-mode" onClick={() => setMode(mode === "essential" ? "advanced" : "essential")}>
            <SlidersHorizontal /><span><strong>{mode === "essential" ? "Modo esencial" : "Modo avanzado"}</strong><small>{mode === "essential" ? "Solo lo necesario" : "Todos los controles"}</small></span>
          </button>
        </div>
      </aside>

      <header className="workspace-topbar">
        <button type="button" className="global-search-trigger" onClick={() => setCommandOpen(true)}>
          <Search /><span><strong>Buscar o ir a…</strong><small>Funciones, acciones y accesos rápidos</small></span><kbd>{platformShortcut()}</kbd>
        </button>
        <div className="workspace-topbar__actions">
          {(!online || queued > 0) && <span className="sync-status" title={online ? `${queued} cambios pendientes` : "Sin conexión"}>{online ? <Wifi /> : <WifiOff />}{online ? queued : "Sin conexión"}</span>}
          <button type="button" className="topbar-icon-button topbar-icon-button--alerts" onClick={(event) => { alertsTriggerRef.current = event.currentTarget; setAlertsOpen((current) => !current); }} aria-haspopup="dialog" aria-expanded={alertsOpen} aria-label={`Abrir avisos${unread ? `, ${unread} sin leer` : ""}`}><Bell />{unread > 0 && <i>{unread > 99 ? "99+" : unread}</i>}</button>
          <button type="button" className="account-trigger" onClick={(event) => toggleProfile(event.currentTarget)} aria-haspopup="dialog" aria-controls="account-popover" aria-expanded={profileOpen}><span className="avatar avatar--small">{user?.name.slice(0, 2).toUpperCase()}</span><span><strong>{user?.name}</strong><small>{roleLabel}</small></span><MoreHorizontal /></button>
        </div>
      </header>

      <header className="mobile-header">
        <Link to="/app" aria-label="Cochegest, ir al inicio"><Logo /></Link>
        <div className="mobile-header__actions">
          {!online && <span className="offline-dot" title="Sin conexión" />}
          <button type="button" className="icon-button" onClick={() => setCommandOpen(true)} aria-label={`Buscar funciones, atajo ${platformShortcut()}`}><Search /></button>
          <button type="button" className="icon-button mobile-bell" onClick={(event) => { alertsTriggerRef.current = event.currentTarget; setAlertsOpen((current) => !current); }} aria-haspopup="dialog" aria-expanded={alertsOpen} aria-label={`Abrir notificaciones${unread ? `, ${unread} sin leer` : ""}`}><Bell />{unread > 0 && <i>{unread > 99 ? "99+" : unread}</i>}</button>
          <button type="button" className="account-trigger account-trigger--mobile" onClick={(event) => toggleProfile(event.currentTarget)} aria-label="Abrir menú de cuenta" aria-haspopup="dialog" aria-controls="account-popover" aria-expanded={profileOpen}><span className="avatar avatar--small">{user?.name.slice(0, 2).toUpperCase()}</span><small>Cuenta</small></button>
        </div>
      </header>

      {profileOpen && <div id="account-popover" ref={profileMenuRef} className="profile-popover" role="dialog" aria-label="Menú de cuenta">
        <header><span className="avatar">{user?.name.slice(0, 2).toUpperCase()}</span><div><strong>{user?.name}</strong><small>{user?.tenant.name} · {roleLabel}</small></div></header>
        <Link to="/app/cuenta" onClick={() => setProfileOpen(false)}><Users />Mi cuenta y seguridad</Link>
        <button type="button" className="profile-popover__logout" onClick={requestLogout}><LogOut />Cerrar sesión</button>
        <section>
          <span>Interfaz</span>
          <div className="mode-segment" role="group" aria-label="Nivel de detalle de la interfaz">
            <button type="button" className={mode === "essential" ? "is-active" : ""} onClick={() => chooseMode("essential")} aria-pressed={mode === "essential"}>Esencial</button>
            <button type="button" className={mode === "advanced" ? "is-active" : ""} onClick={() => chooseMode("advanced")} aria-pressed={mode === "advanced"}>Avanzado</button>
          </div>
          <small>{mode === "essential" ? "Oculta datos técnicos y deja las tareas principales." : "Muestra fiscalidad, integraciones y controles completos."}</small>
        </section>
        <section>
          <span>Tema</span>
          <div className="mode-segment" role="group" aria-label="Tema de color">
            <button type="button" className={theme === "light" ? "is-active" : ""} onClick={() => setTheme("light")} aria-pressed={theme === "light"}><Sun />Claro</button>
            <button type="button" className={theme === "dark" ? "is-active" : ""} onClick={() => setTheme("dark")} aria-pressed={theme === "dark"}><Moon />Oscuro</button>
            <button type="button" className={theme === "system" ? "is-active" : ""} onClick={() => setTheme("system")} aria-pressed={theme === "system"}><MonitorSmartphone />Auto</button>
          </div>
          <small>{theme === "system" ? "Sigue la preferencia de tu dispositivo." : theme === "dark" ? "Descanso visual con el tema carbono." : "Tema papel, el de siempre."}</small>
        </section>
        <section>
          <span>Lectura</span>
          <div className="mode-segment" role="group" aria-label="Tamaño de letra">
            <button type="button" className={textSize === "normal" ? "is-active" : ""} onClick={() => setTextSize("normal")} aria-pressed={textSize === "normal"}><Type />Normal</button>
            <button type="button" className={textSize === "large" ? "is-active" : ""} onClick={() => setTextSize("large")} aria-pressed={textSize === "large"}><CaseSensitive />Grande</button>
          </div>
          <small>{textSize === "large" ? "Letra y controles más grandes para leer sin esfuerzo." : "El tamaño estándar de la aplicación."}</small>
        </section>
        {user?.role === "admin" && <Link to="/app/configuracion" onClick={() => setProfileOpen(false)}><Settings />Configuración</Link>}
        <button type="button" onClick={() => { setProfileOpen(false); setCommandOpen(true); }}><Search />Buscar una función <kbd>{platformShortcut()}</kbd></button>
      </div>}

      <main className="app-main" id="main-content" ref={mainRef} tabIndex={-1} key={location.pathname}>{children}</main>

      <nav className="bottom-nav" aria-label="Navegación principal">
        {primaryNavItems.filter((item) => item.bottom !== false).map(({ id, to, label, bottomLabel, icon: Icon }) => {
          const active = isMobileDestinationActive(id, location.pathname, location.search, mode);
          return <Link key={id} to={to} className={`bottom-nav__item ${active ? "bottom-nav__item--active" : ""}`} aria-current={active ? "page" : undefined}>
            <Icon aria-hidden="true" />
            <span>{bottomLabel ?? label}</span>
          </Link>;
        })}
      </nav>
      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} onNewVehicle={() => setQuickAddOpen(true)} onLogout={requestLogout} />
      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
      <QuickAddModal open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
      <NotificationCenter open={alertsOpen} onClose={() => setAlertsOpen(false)} triggerRef={alertsTriggerRef} />
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} · Cochegest`;
  }, [title]);
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="page-header__description">{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function VehiclePhoto({ vehicleId, photoId, versionId, label }: { vehicleId: string; photoId: string; versionId?: string | null; label: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const hostRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || visible) return;
    if (!("IntersectionObserver" in window)) { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setVisible(true);
      observer.disconnect();
    }, { rootMargin: "320px" });
    observer.observe(host);
    return () => observer.disconnect();
  }, [visible]);
  useEffect(() => {
    if (!visible) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    const path = versionId
      ? `/photo-versions/${versionId}/file?variant=thumbnail`
      : `/vehicles/${vehicleId}/photos/${photoId}/file?variant=thumbnail`;
    void privateObjectUrl(path).then((next) => {
      if (cancelled) { URL.revokeObjectURL(next); return; }
      objectUrl = next;
      setSrc(next);
    }).catch(() => {
      if (cancelled) return;
      setSrc(null);
      setFailed(true);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [vehicleId, photoId, versionId, visible]);
  return (
    <span ref={hostRef} className="vehicle-visual__media" aria-hidden={!src}>
      {src
        ? <img className="vehicle-visual__photo" src={src} alt={label} width="640" height="480" loading="lazy" decoding="async" fetchPriority="low" />
        : failed
          ? <span className="vehicle-visual__fallback" aria-hidden="true"><CarFront /></span>
          : <span className="vehicle-visual__skeleton" aria-hidden="true" />}
    </span>
  );
}

export function VehicleVisual({ tone, make, model, compact = false, vehicleId, coverPhotoId, coverPhotoVersionId }: { tone: string; make?: string | null; model?: string | null; compact?: boolean; vehicleId?: string; coverPhotoId?: string | null; coverPhotoVersionId?: string | null }) {
  const label = `Imagen de ${make ?? "vehículo"} ${model ?? ""}`;
  const hasPhoto = Boolean(vehicleId && coverPhotoId);
  return (
    <div className={`vehicle-visual vehicle-visual--${tone} ${compact ? "vehicle-visual--compact" : ""} ${hasPhoto ? "vehicle-visual--has-photo" : ""}`} role="img" aria-label={label}>
      <span className="vehicle-visual__glow" />
      <CarFront aria-hidden="true" />
      {vehicleId && coverPhotoId && <VehiclePhoto vehicleId={vehicleId} photoId={coverPhotoId} versionId={coverPhotoVersionId} label={label} />}
      {!compact && !hasPhoto && <span className="vehicle-visual__label">{make ?? "Vehículo"}<strong>{model ?? "sin modelo"}</strong></span>}
    </div>
  );
}

export function VehicleCard({ vehicle, compact = false, to }: { vehicle: VehicleListItem; compact?: boolean; to?: string }) {
  const { mode } = useUiPreferences();
  const title = [vehicle.make, vehicle.model].filter(Boolean).join(" ") || vehicle.registration || "Vehículo sin identificar";
  return (
    <Link to={to ?? `/app/stock/${vehicle.id}`} className={`vehicle-card vehicle-card--${mode} ${compact ? "vehicle-card--compact" : ""}`}>
      <VehicleVisual tone={vehicle.coverTone} make={vehicle.make} model={vehicle.model} compact={compact} vehicleId={vehicle.id} coverPhotoId={vehicle.coverPhotoId} coverPhotoVersionId={vehicle.coverPhotoVersionId} />
      <div className="vehicle-card__body">
        <div className="vehicle-card__top">
          <StatusBadge status={vehicle.status} />
          {mode === "advanced" && vehicle.taxFlow && <span className="status-chip status-chip--neutral">{vehicle.taxFlow === "rebu" ? "REBU" : vehicle.taxFlow === "general" ? "IVA general" : "Comisión"}</span>}
          {vehicle.itpExemption && (vehicle.itpExemption.status === "expired" || (vehicle.itpExemption.status === "provisional" && (vehicle.itpExemption.daysRemaining ?? 999) <= 90)) && <span className="alert-count"><AlertTriangle />{vehicle.itpExemption.status === "expired" ? (mode === "advanced" ? "ITP vencida" : "Plazo fiscal vencido") : mode === "advanced" ? `ITP ${vehicle.itpExemption.daysRemaining} d` : `${vehicle.itpExemption.daysRemaining} d de plazo`}</span>}
          {vehicle.alertCount > 0 && <span className="alert-count" aria-label={`${vehicle.alertCount} ${vehicle.alertCount === 1 ? "aviso pendiente" : "avisos pendientes"}`}><AlertTriangle aria-hidden="true" />{vehicle.alertCount}</span>}
        </div>
        <h3>{title}</h3>
        <p>{vehicle.version ?? vehicle.registration ?? vehicle.vin ?? "Ficha por completar"}</p>
        <div className="vehicle-card__meta">
          <span><Gauge />{vehicle.mileage === null ? "Kilometraje pendiente" : `${vehicle.mileage.toLocaleString("es-ES")} km`}</span>
          <span><CircleGauge />{vehicle.daysInStock} {vehicle.daysInStock === 1 ? "día" : "días"}</span>
        </div>
        <div className="vehicle-card__footer">
          <div><small>Precio objetivo</small><strong className={vehicle.targetPriceCents === null ? "value--pending" : ""}>{vehicle.targetPriceCents === null ? "Pendiente" : formatCurrency(vehicle.targetPriceCents)}</strong></div>
          <div><small>Margen neto previsto</small><strong className={vehicle.estimatedNetMarginCents === null ? "value--pending" : vehicle.estimatedNetMarginCents >= 0 ? "positive" : "negative"}>{vehicle.estimatedNetMarginCents === null ? "Se calculará" : formatCurrency(vehicle.estimatedNetMarginCents)}</strong></div>
        </div>
      </div>
      <ChevronRight className="vehicle-card__chevron" aria-hidden="true" />
    </Link>
  );
}

export function MetricCard({ label, value, detail, icon: Icon, tone = "default" }: { label: string; value: string; detail?: string; icon: LucideIcon; tone?: "default" | "accent" | "dark" }) {
  return (
    <article className={`metric-card metric-card--${tone}`}>
      <span className="metric-card__icon"><Icon aria-hidden="true" /></span>
      <div><p>{label}</p><strong>{value}</strong>{detail && <small>{detail}</small>}</div>
    </article>
  );
}

export function LoadingView({ label = "Cargando tu operativa…" }: { label?: string }) {
  return <div className="loading-view" role="status" aria-live="polite" aria-busy="true"><div className="loading-view__content"><div className="loading-view__skeleton" aria-hidden="true"><i /><i /><i /><i /></div><p>{label}</p></div></div>;
}

export function EmptyState({ icon: Icon = Search, title, description, action, headingLevel = 3 }: { icon?: LucideIcon; title: string; description: string; action?: ReactNode; headingLevel?: 1 | 2 | 3 }) {
  const Heading = headingLevel === 1 ? "h1" : headingLevel === 2 ? "h2" : "h3";
  return <div className="empty-state" role="status"><span><Icon aria-hidden="true" /></span><Heading>{title}</Heading><p>{description}</p>{action}</div>;
}

export function ErrorState({ title = "No hemos podido abrir esta pantalla", description = "La información no se ha perdido. Comprueba la conexión e inténtalo de nuevo.", onRetry }: { title?: string; description?: string; onRetry?(): void }) {
  return <div className="state-view state-view--error" role="alert"><div className="state-view__content"><span className="state-view__icon"><AlertTriangle aria-hidden="true" /></span><h2>{title}</h2><p>{description}</p>{onRetry && <button type="button" className="button button--primary" onClick={onRetry}><RefreshCw />Volver a intentar</button>}</div></div>;
}

export function InlineNotice({ title, description, onRetry }: { title: string; description: string; onRetry?(): void }) {
  return <section className="inline-notice" role="status"><span className="inline-notice__icon"><AlertTriangle aria-hidden="true" /></span><div><strong>{title}</strong><p>{description}</p></div>{onRetry && <button type="button" className="button button--secondary" onClick={onRetry}><RefreshCw />Reintentar</button>}</section>;
}

export function GuidedTaskBanner({ title, description, meta, onCancel }: {
  title: string;
  description: string;
  meta?: string;
  onCancel(): void;
}) {
  const titleId = useId();
  return <section className="guided-task" aria-labelledby={titleId}>
    <span className="guided-task__icon"><MousePointerClick aria-hidden="true" /></span>
    <div className="guided-task__copy"><small>Tarea guiada</small><h2 id={titleId}>{title}</h2><p>{description}</p>{meta && <span>{meta}</span>}</div>
    <button type="button" className="button button--secondary" onClick={onCancel}><X aria-hidden="true" />Ver todo</button>
  </section>;
}

export function SectionErrorState({ title, description, onRetry }: { title: string; description: string; onRetry?(): void }) {
  return <section className="section-error-state" role="alert"><span className="section-error-state__icon"><AlertTriangle aria-hidden="true" /></span><div><strong>{title}</strong><p>{description}</p></div>{onRetry && <button type="button" className="button button--secondary" onClick={onRetry}><RefreshCw />Reintentar</button>}</section>;
}

export function TechnicalDetails({ items, summary = "Ver detalles técnicos" }: {
  items: Array<{ label: string; value: string | number | null | undefined }>;
  summary?: string;
}) {
  const visibleItems = items.filter((item) => item.value !== null && item.value !== undefined && String(item.value).trim().length > 0);
  if (!visibleItems.length) return null;
  return <details className="technical-details"><summary>{summary}</summary><dl>{visibleItems.map((item) => <div key={`${item.label}-${item.value}`}><dt>{item.label}</dt><dd><code>{item.value}</code></dd></div>)}</dl></details>;
}

export function Modal({ open, onClose, title, children, size = "normal", initialFocusRef, returnFocusRef }: {
  open: boolean;
  onClose(): void;
  title: string;
  children: ReactNode;
  size?: "normal" | "wide";
  initialFocusRef?: RefObject<HTMLElement | null>;
  returnFocusRef?: RefObject<HTMLElement | null>;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusableSelector = "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])";
    let preferredFocusApplied = false;
    const focusFirst = () => {
      const autofocus = dialogRef.current?.querySelector<HTMLElement>("[autofocus]");
      const firstField = dialogRef.current?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled]), textarea:not([disabled])");
      const first = dialogRef.current?.querySelector<HTMLElement>(focusableSelector);
      (autofocus ?? firstField ?? first ?? dialogRef.current)?.focus();
    };
    let frame = 0;
    const recoverFocus = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const active = document.activeElement;
        const preferred = initialFocusRef?.current;
        const preferredReady = preferred && !preferred.matches(":disabled") && preferred.getClientRects().length > 0;
        const activeHasInput = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement
          ? active.value.length > 0
          : false;
        if (!preferredFocusApplied && preferredReady && !activeHasInput) {
          preferred.focus();
          preferredFocusApplied = true;
          return;
        }
        if (active instanceof HTMLElement && dialog.contains(active) && active !== dialog) return;
        focusFirst();
      });
    };
    recoverFocus();
    // Some dialogs fetch their options after opening. When their disabled
    // fields become usable, recover focus if it was left on the document.
    const observer = new MutationObserver(recoverFocus);
    if (dialogRef.current) observer.observe(dialogRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled"],
    });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      (returnFocusRef?.current ?? previousFocus)?.focus();
    };
  }, [initialFocusRef, open, returnFocusRef]);
  if (!open) return null;
  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} tabIndex={-1} className={`modal modal--${size}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header><h2 id="modal-title">{title}</h2><button className="icon-button" onClick={onClose} aria-label="Cerrar"><X /></button></header>
        {children}
      </section>
    </div>,
    document.body,
  );
}

export function QuickAddModal(props: { open: boolean; onClose(): void; onCreated?(): void }) {
  return <VehicleIntakeModal {...props} />;
}

export function FloatingAddButton({ onClick }: { onClick(): void }) {
  return <button className="floating-add" onClick={onClick} aria-label="Dar de alta un vehículo"><Plus /><span>Nuevo vehículo</span></button>;
}

export const moduleCards = [
  { icon: Users, title: "Clientes y respuestas automáticas", text: "Convierte los correos de portales en consultas listas para responder.", phase: "Fase 2", to: "/app/leads" },
  { icon: WalletCards, title: "Ventas, contratos y facturas", text: "Cierra la operación completa en una sola secuencia.", phase: "Fase 2", to: "/app/leads" },
  { icon: LayoutGrid, title: "Publicación multicanal", text: "Cambia precio y estado una sola vez para todos los portales.", phase: "Fase 4", to: "/app/stock" },
  { icon: Settings, title: "Transferencias y escaparate", text: "Swipoo, firma y ficha pública con QR por vehículo.", phase: "Fase 3", to: "/app/configuracion" },
  { icon: CircleGauge, title: "Inteligencia de mercado", text: "Precio recomendado y tiempo de venta con datos agregados.", phase: "Fase 4", to: "/app/inteligencia" },
];

export function LockedModuleCard({ item }: { item: (typeof moduleCards)[number] }) {
  const Icon = item.icon;
  return <Link to={item.to} className="locked-module available-module"><span><Icon /></span><div><small>{item.phase}</small><h3>{item.title}</h3><p>{item.text}</p></div><ChevronRight /></Link>;
}

export const viewIcons = { list: Menu, gallery: LayoutGrid, kanban: CircleGauge };
export { AlertTriangle, Bell, Camera, CircleGauge, FileText, Gauge, LayoutGrid, Plus, Search, WalletCards };
